/**
 * إطار الصفحات النظامية — الشروط · الخصوصية · إخلاء المسؤولية.
 *
 * صفحة نظامية تُقرأ لا تُتصفَّح: عمود واحد ضيّق، سطر طويل مريح، وترقيم بنود
 * ظاهر ليُشار إليه («البند ٤-٢»). لا بطاقات ولا أعمدة — الأعمدة تُقارَن،
 * والنص النظامي يُتتبَّع.
 *
 * تاريخ السريان ظاهر في الأعلى لا في الأسفل: أول ما يسأل عنه القارئ الجادّ
 * هو «أي نسخة أقرأ؟».
 */

import type { ReactNode } from 'react';
import { color } from '@/design/tokens';
import { PageHeader } from '@/components/primitives/PageHeader';

/** جهة التواصل النظامية — مكان واحد يُغيَّر منه. */
export const LEGAL_CONTACT = 'Rakan.abduldaim@hotmail.com';

/** تاريخ سريان النسخة الحالية من الوثائق النظامية. */
export const LEGAL_EFFECTIVE_DATE = '٢٠٢٦-٠٨-١٩';

export function LegalSection({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section style={{ marginBottom: 34 }}>
      <h2
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          margin: '0 0 12px',
          fontSize: 16,
          fontWeight: 600,
          color: color.text,
        }}
      >
        <span
          className="nq-mono"
          style={{ fontSize: 12, color: color.teal, flex: '0 0 auto' }}
        >
          {number}
        </span>
        {title}
      </h2>
      <div style={{ fontSize: 14, lineHeight: 1.95, color: color.textBody }}>{children}</div>
    </section>
  );
}

/** فقرة نظامية — المسافة بين الفقرات جزء من القراءة لا زينة. */
export function P({ children }: { children: ReactNode }): JSX.Element {
  return <p style={{ margin: '0 0 12px' }}>{children}</p>;
}

/** قائمة بنود — للتعداد الذي يُشار إليه. */
export function L({ items }: { items: ReactNode[] }): JSX.Element {
  return (
    <ul style={{ margin: '0 0 12px', paddingInlineStart: 20 }}>
      {items.map((item, i) => (
        <li key={i} style={{ marginBottom: 7 }}>
          {item}
        </li>
      ))}
    </ul>
  );
}

/** تنبيه داخل النص — لِما لا يجوز أن يُقرأ مروراً. */
export function Notice({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div
      style={{
        margin: '0 0 14px',
        padding: '13px 15px',
        background: color.tealFaint,
        border: `1px solid ${color.tealBorder}`,
        borderRadius: 8,
        fontSize: 13.5,
        lineHeight: 1.85,
        color: color.textBody,
      }}
    >
      {children}
    </div>
  );
}

export function LegalPage({
  stamp,
  title,
  subtitle,
  testId,
  children,
}: {
  stamp: string;
  title: string;
  subtitle: string;
  testId: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div data-testid={testId}>
      <PageHeader
        stamp={stamp}
        title={title}
        subtitle={subtitle}
        meta={
          <span className="nq-micro">
            سارية من {LEGAL_EFFECTIVE_DATE} · أي تعديل يُنشر هنا بتاريخ جديد
          </span>
        }
      />

      <article style={{ maxWidth: 720, padding: '30px 0 60px' }}>{children}</article>
    </div>
  );
}
